import type { OnDestroy } from '@angular/core';
import { Directive, effect, ElementRef, input, signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { map, Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LocalStorageService } from '@core/common-legacy';
import { ScrollableDirective } from '@core/user-interface-legacy';

/**
 * This directive is used to save the intersection of the CV with the top of the viewport.
 * It is used to scroll the top of the CV into view when user navigates between candidates.
 */
@Directive({
  selector: '[rtCvScroller]',
  standalone: true,
})
export class CvScrollerDirective implements OnDestroy {
  private static VIEWPORT_TOP_CV_INTERSECTION_LS_KEY = 'viewport-top-cv-intersection';
  private static CONTENT_RESIZE_DELAY = 60;

  private readonly scrollableDirective = inject(ScrollableDirective);
  private readonly localStorage = inject(LocalStorageService);
  private readonly cvElRef = inject(ElementRef);

  public rtCvScrollerProfileHeaderHeight = input.required<number>({ alias: 'profileHeaderHeight' });

  private isViewportTopCVIntersection = toSignal(
    this.scrollableDirective.y$.pipe(
      map(() => {
        {
          const cvEl = this.cvElRef?.nativeElement;

          if (!cvEl) {
            return;
          }

          const cvElRect: DOMRect = cvEl.getBoundingClientRect();
          return (
            cvElRect.top < this.rtCvScrollerProfileHeaderHeight() &&
            cvElRect.bottom > this.rtCvScrollerProfileHeaderHeight()
          );
        }
      }),
    ),
  );

  private ngOnDestroy$ = new Subject<void>();
  private saveEnabled = signal(false);

  constructor() {
    effect(() => {
      if (this.saveEnabled()) {
        this.saveViewportTopCVIntersection();
      }
    });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public scrollToCVTopIfNeeded(): void {
    timer(CvScrollerDirective.CONTENT_RESIZE_DELAY)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        if (this.localStorage.get(CvScrollerDirective.VIEWPORT_TOP_CV_INTERSECTION_LS_KEY, false)) {
          this.scrollableDirective.scrollTo({
            top: this.cvElRef.nativeElement.offsetTop - this.rtCvScrollerProfileHeaderHeight(),
            left: 0,
          });
        }
        this.saveEnabled.set(true);
      });
  }

  private saveViewportTopCVIntersection(): void {
    const isIntersection = this.isViewportTopCVIntersection();

    if (isIntersection === undefined) {
      return;
    }

    this.localStorage.set(CvScrollerDirective.VIEWPORT_TOP_CV_INTERSECTION_LS_KEY, isIntersection);
  }
}
